import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/styles.css";

import heroVideo from "../Videos/silentVideo.mp4";

const VEHICLES = [
  {
    title: "Travel Trailers",
    desc: "Weekend-ready campers with flexible pickup options.",
    tag: "Sleeps 4–8",
    categories: ["camp"],
    img: "/images/vehicles/Trailers.jpg",
  },
  {
    title: "Side-by-Sides & Golf Carts",
    desc: "Trail machines built for group adventure.",
    tag: "2–6 seats",
    categories: ["trail", "winter"],
    img: "/images/vehicles/SxS.jpg",
  },
  {
    title: "ATVs",
    desc: "Quick, capable rides for exploring new terrain.",
    tag: "All-terrain",
    categories: ["trail", "winter"],
    img: "/images/vehicles/ATV.jpg",
  },
  {
    title: "Boats",
    desc: "From calm cruises to big-water days.",
    tag: "Lake-ready",
    categories: ["water"],
    img: "/images/vehicles/Marine.jpg",
  },
  {
    title: "Waverunners",
    desc: "Fast, fun, and easy to launch.",
    tag: "Jet-powered",
    categories: ["water"],
    img: "/images/vehicles/Waverunner.jpg",
  },
  {
    title: "Snowmobiles",
    desc: "Winter access to the places summer can’t reach.",
    tag: "Deep snow",
    categories: ["winter"],
    img: "/images/vehicles/Snowmobile.jpg",
  },
];

const TIERS = [
  {
    key: "explore",
    name: "Explore",
    days: "15 Days Per Year",
    annualPrice: "$2,199",
    monthlyPrice: "$199 / M",
    monthlyNote: "+ Tax OAC*",
    tagline: "Perfect for weekend getaways",
    featured: false,
    access: {
      headline: "All Categories",
      detail: "Standard & Compact Models",
    },
  },
  {
    key: "adventure",
    name: "Adventure",
    days: "22 Days Per Year",
    annualPrice: "$3,849",
    monthlyPrice: "$349 / M",
    monthlyNote: "+ Tax OAC*",
    tagline: "Best for families and active outdoor users.",
    featured: true,
    badge: "Most Popular",
    access: {
      headline: "All Categories",
      detail: "Recreational Models",
    },
  },
  {
    key: "elite",
    name: "Elite",
    days: "29 Days Per Year",
    monthlyPrice: "$599 / M",
    monthlyNote: "+ Tax OAC*",
    annualPrice: "$6,599",
    tagline: "Exceptional Value, Premium Convenience.",
    featured: false,
    access: {
      headline: "All Categories",
      detail: "Premium & Performance Models",
    },
  },
];

const FEATURES = [
  { label: "Vehicle Access", values: ["Compact", "Recreation", "Premium"] },
  { label: "Insurance", values: ["Included", "Included", "Included"] },
  { label: "GPS Tracking", values: ["✓", "✓", "✓"] },
  { label: "24/7 Support", values: ["✓", "✓", "✓"] },
  { label: "Concierge Support", values: ["✓", "✓", "✓"] },
  { label: "Maintenance", values: ["✓", "✓", "✓"] },
  { label: "Storage", values: ["✓", "✓", "✓"] },
  { label: "Cleaning", values: ["✓", "✓", "✓"] },
];

function Cell({ value }) {
  const isCheck = value === "✓";
  const isDash = value === "—";
  return (
    <span
      className={`ctCell ${isCheck ? "is-check" : ""} ${
        isDash ? "is-dash" : ""
      }`}
    >
      {isCheck ? "✓" : value}
    </span>
  );
}

export default function HomePage() {
  const [activeFilter, setActiveFilter] = useState("all");

  // --- CTA FORM state + submit (SendGrid via /api/send-lead) ---
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "membership",
    season: "all",
    vehicles: "",
  });

  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState(null); // null | "success" | "error"

  const handleChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setStatus(null);

    try {
      const res = await fetch("/api/send-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("Request failed");

      setStatus("success");
      setForm({
        name: "",
        email: "",
        phone: "",
        interest: "membership",
        season: "all",
        vehicles: "",
      });
    } catch (err) {
      console.error(err);
      setStatus("error");
    } finally {
      setIsSending(false);
    }
  };

  const filteredVehicles = useMemo(() => {
    if (activeFilter === "all") return VEHICLES;
    return VEHICLES.filter((v) => v.categories.includes(activeFilter));
  }, [activeFilter]);

  const defaultKey = TIERS.find((t) => t.featured)?.key || TIERS[0].key;
  const [selectedTier, setSelectedTier] = useState(defaultKey);

  const selectedIndex = useMemo(
    () =>
      Math.max(
        0,
        TIERS.findIndex((t) => t.key === selectedTier)
      ),
    [selectedTier]
  );

  const activeTier = TIERS[selectedIndex];

  return (
    <div className="page">
      <main id="top">
        {/* HERO (big enough for video) */}
        <section className="hero" aria-label="Hero">
          <video
            className="hero__video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            
          >
            <source src={heroVideo} type="video/mp4" />
          </video>

          <div className="container hero__content">
            <h1 className="hero__title">
              <span className="hero__word hero__word--try">Try.</span>{" "}
              <span className="hero__word hero__word--play">Play.</span>{" "}
              <span className="hero__word hero__word--go">Go.</span>
            </h1>
          </div>

          <a
            className="hero__scroll"
            href="#vehicles"
            aria-label="Scroll to vehicles"
          >
            <span />
          </a>
        </section>

        {/* Who we are */}
        <section className="section section--muted">
          <div className="container">
            <div className="who who--full">
              <div className="who__copy">
                <div>The Club</div>
                <br />
                <h2 className="who__title">A membership built for real life</h2>

                <p className="who__text">
                  We created the club because too many people spend their lives
                  waiting. Waiting to have enough money. Waiting to have enough
                  time. Waiting to live their dreams. Access should be for
                  everyone.
                  {<br />} {<br />}The club is for members who want to
                  experience what freedom feels like in the great outdoors.
                  Memories don’t happen in the living room. They become the
                  stories your kids will tell their kids. Every season deserves
                  to be lived. Every adventure deserves to be possible. Every
                  dream deserves action—not “someday.”
                  {<br />} {<br />}Freedom isn’t about what you own. It’s about
                  what you experience.
                </p>

                <p className="who__text">
                  Our club was designed with all this in mind. Bringing outdoor
                  adventures to those who seek it.
                  {<br />} {<br />} - TPG Team
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Video (below Who we are) */}
        <section className="section section--muted">
          <div className="container">
            <div className="videoBlock">
              <div className="videoBlock__copy">
                <div className="videoBlock__eyebrow">Watch</div>
                <h2 className="videoBlock__title">Why the club works!</h2>
                <p className="videoBlock__text"></p>
              </div>

              <div className="videoBlock__frame" aria-label="YouTube video">
                <iframe
                  src="https://www.youtube.com/embed/dbqF5zHbWVg"
                  title="Try Play Go - How it works"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Membership tiers */}
        <section className="section">
          <div className="container">
            <div className="section__head">
              <div>
                <h2 className="section__title">Step 1: Choose Your Club.</h2>
                <p className="section__subtitle">
                  Pick the access level that matches how often you want to get
                  out.
                </p>
              </div>
            </div>

            <div className="ctCards">
              {TIERS.map((t) => (
                <article
                  key={t.key}
                  className={`ctCard ${t.featured ? "is-featured" : ""}`}
                >
                  {t.featured && (
                    <div className="ctCard__badge">
                      {t.badge || "Most Popular"}
                    </div>
                  )}

                  <div className="ctCard__top">
                    <h3 className="ctCard__name">{t.name}</h3>
                    <div className="ctCard__days">{t.days}</div>
                    <p className="ctCard__tag">{t.tagline}</p>
                  </div>

                  <br />
                  <div className="ctPriceBlock">
                    <div className="ctPriceBlock__label">
                      Monthly Payment - 12 Month Term.
                    </div>
                    <div className="ctPriceBlock__value">
                      {t.monthlyPrice}{" "}
                      <span className="ctMuted">{t.monthlyNote}</span>
                    </div>
                  </div>

                  <br />
                  <div className="ctCard__or">OR</div>

                  <div className="ctPrices">
                    <div className="ctPriceBlock">
                      <div className="ctPriceBlock__label">Annual Payment</div>
                      <div className="ctPriceBlock__value">
                        {t.annualPrice} <span className="ctMuted">+ Tax</span>
                      </div>
                    </div>
                  </div>

                  <div className="ctAccess">
                    <div className="ctAccess__title">Vehicle Access</div>
                    <div className="ctAccess__headline">
                      {t.access.headline}
                    </div>
                    <div className="ctAccess__detail">{t.access.detail}</div>
                  </div>

                  <div className="ctCard__actions">
                    <Link
                      className={`btn ${
                        t.featured ? "btn--primary" : "btn--ghost"
                      } ctCard__btn`}
                      to={`/${t.key}`}
                    >
                      Join {t.name}
                    </Link>

                    <Link className="link ctCard__link" to="/clubs">
                      Compare
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* What's included */}
        <section className="section section--muted">
          <div className="container">
            <div className="section__head">
              <div>
                <h2 className="section__title">What’s included</h2>
                <p className="section__subtitle">
                  Every club includes the essentials — compare the details.
                </p>
              </div>
            </div>

            {/* Desktop table */}
            <div
              className="ctTableWrap"
              aria-label="Club tiers comparison table"
            >
              <table className="ctTable">
                <thead>
                  <tr>
                    <th className="ctTable__feature">Feature</th>
                    {TIERS.map((t) => (
                      <th
                        key={t.key}
                        className={`ctTable__plan ${
                          t.featured ? "is-featured" : ""
                        }`}
                      >
                        <div className="ctPlanName">{t.name}</div>
                        <div className="ctPlanMeta">{t.days}</div>
                        <br />
                        {t.featured && (
                          <div className="ctPill">Most Popular</div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {FEATURES.map((row) => (
                    <tr key={row.label}>
                      <th className="ctTable__rowLabel">{row.label}</th>
                      {row.values.map((v, idx) => (
                        <td
                          key={`${row.label}-${idx}`}
                          className="ctTable__cell"
                        >
                          <Cell value={v} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile picker */}
            <div className="ctMobile" aria-label="Mobile club comparison">
              <div
                className="ctTabs"
                role="tablist"
                aria-label="Select club tier"
              >
                {TIERS.map((t) => {
                  const isActive = selectedTier === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`ctTab ${isActive ? "is-active" : ""} ${
                        t.featured ? "is-featured" : ""
                      }`}
                      onClick={() => setSelectedTier(t.key)}
                    >
                      <div className="ctTab__top">
                        <span className="ctTab__name">{t.name}</span>
                        {t.featured && (
                          <span className="ctTab__pill">Popular</span>
                        )}
                      </div>
                      <div className="ctTab__meta">
                        <span className="ctTab__price">{t.monthlyPrice}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <article className="ctMobileCard">
                <div className="ctMobileCard__head">
                  <div>
                    <div className="ctMobileCard__title">{activeTier.name}</div>
                    <div className="ctMobileCard__sub">
                      <span className="ctStrong">{activeTier.annualPrice}</span>{" "}
                      <span className="ctMuted">+ Tax</span> •{" "}
                      <span className="ctStrong">
                        {activeTier.monthlyPrice}
                      </span>{" "}
                      <span className="ctMuted">{activeTier.monthlyNote}</span>
                    </div>
                  </div>

                  <Link
                    className={`btn ${
                      activeTier.featured ? "btn--primary" : "btn--ghost"
                    }`}
                    to="/#join"
                  >
                    Join
                  </Link>
                </div>

                <div className="ctMobileList" role="list">
                  <div className="ctMobileRow" role="listitem">
                    <div className="ctMobileRow__label">Annual Days</div>
                    <div className="ctMobileRow__value">
                      <Cell value={activeTier.days} />
                    </div>
                  </div>

                  {FEATURES.map((row) => (
                    <div
                      key={row.label}
                      className="ctMobileRow"
                      role="listitem"
                    >
                      <div className="ctMobileRow__label">{row.label}</div>
                      <div className="ctMobileRow__value">
                        <Cell value={row.values[selectedIndex]} />
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* VEHICLES */}
        <section id="vehicles" className="section">
          <div className="container">
            <div className="section__head">
              <div>
                <h2 className="section__title">
                  Step 2: Choose your adventure.
                </h2>
                <p className="section__subtitle">
                  Browse the lineup and book what fits your weekend — lake,
                  trail, or snow.
                </p>
              </div>

              <div className="section__tools" aria-label="Vehicle filters">
                <button
                  className={`pill ${
                    activeFilter === "all" ? "is-active" : ""
                  }`}
                  type="button"
                  onClick={() => setActiveFilter("all")}
                >
                  All
                </button>

                <button
                  className={`pill ${
                    activeFilter === "water" ? "is-active" : ""
                  }`}
                  type="button"
                  onClick={() => setActiveFilter("water")}
                >
                  Water
                </button>

                <button
                  className={`pill ${
                    activeFilter === "trail" ? "is-active" : ""
                  }`}
                  type="button"
                  onClick={() => setActiveFilter("trail")}
                >
                  Trail
                </button>

                <button
                  className={`pill ${
                    activeFilter === "camp" ? "is-active" : ""
                  }`}
                  type="button"
                  onClick={() => setActiveFilter("camp")}
                >
                  Camp
                </button>

                <button
                  className={`pill ${
                    activeFilter === "winter" ? "is-active" : ""
                  }`}
                  type="button"
                  onClick={() => setActiveFilter("winter")}
                >
                  Winter
                </button>
              </div>
            </div>

            <div className="grid">
              {filteredVehicles.map((v) => (
                <article key={v.title} className="card">
                  <div className="card__media">
                    <img
                      className="card__img"
                      src={v.img}
                      alt={v.title}
                      loading="lazy"
                    />
                    <div className="card__tag">{v.tag}</div>
                  </div>
                  <div className="card__body">
                    <h3 className="card__title">{v.title}</h3>
                    <p className="card__desc">{v.desc}</p>

                    <br/>

                    <Link to="/vehicles" className="btn btn--ghost card__btn">
                      View Vehicles
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

                 {/* TESTIMONIAL */}
        <section className="section section--muted" aria-label="Testimonial">
          <div className="container">
            <div className="testimonial">
              <div className="testimonial__eyebrow">Testimonial</div>

              <blockquote className="testimonial__quote">
                It’s been about 20 years since I borrowed a friend’s snowmobile
                to go for a ride. I have often thought about buying one but
                couldn’t justify the expense. Thanks to the membership club at
                Try Play Go I can take a sled out without the cost and hassle of
                ownership.
              </blockquote>

              <div className="testimonial__name">Arnold Visser</div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="join" className="cta">
          <div className="container cta__inner">
            <div className="cta__copy">
              <div className="cta__badge">Club Access</div>
              <h2 className="section__title">Step 3: Enjoy the outdoors.</h2>
              <br />
              <h2 className="cta__title">Let's Get Started!</h2>
              <p className="cta__text">
                Enter your information and let our Concierge begin building your
                next adventure.
              </p>
            </div>

            <form
              className="ctaForm"
              onSubmit={handleSubmit}
              aria-label="Membership inquiry form"
            >
              <div className="ctaForm__grid">
                <div className="field">
                  <label className="field__label" htmlFor="name">
                    Name
                  </label>
                  <input
                    id="name"
                    className="field__input"
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    className="field__input"
                    type="email"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="phone">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    className="field__input"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={form.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="interest">
                    I’m interested in
                  </label>
                  <select
                    id="interest"
                    className="field__input"
                    value={form.interest}
                    onChange={handleChange}
                  >
                    <option value="membership">Club options</option>
                    <option value="explore">Explore</option>
                    <option value="adventure">Adventure</option>
                    <option value="elite">Elite</option>
                  </select>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="season">
                    Primary season
                  </label>
                  <select
                    id="season"
                    className="field__input"
                    value={form.season}
                    onChange={handleChange}
                  >
                    <option value="all">All seasons</option>
                    <option value="summer">Summer</option>
                    <option value="spring">Spring</option>
                    <option value="fall">Fall</option>
                    <option value="winter">Winter</option>
                  </select>
                </div>

                <div className="field field--full">
                  <label className="field__label" htmlFor="vehicles">
                    Vehicle types (optional)
                  </label>
                  <input
                    id="vehicles"
                    className="field__input"
                    type="text"
                    placeholder="Travel trailer, boat, ATV, side-by-side…"
                    value={form.vehicles}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <button
                className="btn btn--primary ctaForm__btn"
                type="submit"
                disabled={isSending}
              >
                {isSending ? "Sending..." : "Request membership info"}
              </button>

              {status === "success" && (
                <p className="ctaForm__fineprint" role="status">
                  ✅ Thanks! We’ll respond with options and next steps.
                </p>
              )}

              {status === "error" && (
                <p className="ctaForm__fineprint" role="status">
                  ❌ Something went wrong. Please try again.
                </p>
              )}

              {!status && (
                <p className="ctaForm__fineprint">
                  We’ll respond with options and next steps.
                </p>
              )}
            </form>
          </div>
        </section>

        {/* FAQ (simple) */}
        <section id="faq" className="section">
          <div className="container">
            <h2 className="section__title">
              You've Got Questions? We've Got Answers!
            </h2>
            <div className="faq">
              <details className="faq__item">
                <summary className="faq__q">
                  What types of vehicles are available?
                </summary>
                <div className="faq__a">
                  Our Woodstock location offers:\n- Travel trailers, Toy haulers
                  and fifth wheels- Powersports: ATVs, Side By Sides, Golf Carts
                  and Snowmobiles- Marine: Boats and Personal watercrafts
                </div>
              </details>
              <details className="faq__item">
                <summary className="faq__q">
                  What's included in my membership fee vs. What costs extra?
                </summary>
                <div className="faq__a">
                  There are NO additional fees on top of your membership.
                  However, we do offer Add – On products and services. Your
                  membership fee includes: - Your allotted days of vehicle
                  access - GPS tracking - Basic cleaning between useages -
                  Standard maintenance and upkeep - Daily Insurance Additional
                  costs may include: - Fuel/gas (return with same fuel level) -
                  Security Deposit (Pre Authorized Hold - Only charged if damage
                  occurs). - Inter-Provincial Travel Insurance (If Travelling
                  outside of Ontario). - Damage beyond normal wear and tear.
                  Late return fees - Deep cleaning fees if vehicle is returned
                  excessively dirty - Optional add-ons (specific equipment,
                  delivery service and other).
                </div>
              </details>
              <details className="faq__item">
                <summary className="faq__q">
                  Do I need any special license to operate any specific
                  vehicles?
                </summary>
                <div className="faq__a">
                  Yes, a Pleasure Craft Operator Card (PCOC) is recommended for
                  all boat and watercraft members. If you don’t have one, you
                  can get certified online at boaterexam.com. But don’t worry!
                  If you don’t have a boating license, we can often provide a
                  temporary boater checklist and orientation to get you on the
                  water legally and safely. Some conditions and product
                  limitations may apply.
                </div>
              </details>
            </div>
            <br />
            <Link to="/faq" className="btn btn--primary">
              See All
            </Link>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer__inner">
          <div className="footer__left">
            <div className="brand brand--small">
              <img
                src="/images/logo.png"
                alt="Recreation Memberships"
                className="brand__logo"
              />
            </div>
            <p className="footer__meta">
              © {new Date().getFullYear()} All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
